import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  get_task_data,
  get_all_employee_task_report,
} from "../../services/employeeApi";
import { formatDate } from "../../utils/date_utils";
import { Form, Button, Nav, Tab } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import BasicTable from "../../components/UiComponent/customTable";
import { GetUserInfo, hasAccess } from "../../utils/permissionChecker";
import exportToExcel from "../../utils/exportToExcel";
const AdminTaskReport = () => {
  const { t } = useTranslation();
  const user = GetUserInfo();
  const today = new Date();
  const formatYYYYMMDD = (date) => date.toISOString().split("T")[0];
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const [fromDate, setfromDate] = useState(formatYYYYMMDD(sevenDaysAgo));
  const [toDate, settoDate] = useState(formatYYYYMMDD(today));
  const [taskId, setTaskId] = useState("");
  const {
    data: tasks = [],
    isLoading: tasksLoading,
    isError: tasksError,
    error: tasksErrorObj,
  } = useQuery({
    queryKey: ["tasks-dropdown"],
    queryFn: get_task_data,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const {
    data: reportData = {},
    isLoading: reportLoading,
    isError: reportError,
    error: reportErrorObj,
  } = useQuery({
    queryKey: ["admin-task-report", taskId, fromDate, toDate],
    queryFn: () => get_all_employee_task_report(fromDate, toDate, taskId),
    enabled: !!taskId,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (tasks.length > 0 && !taskId) {
      setTaskId(tasks[0].id);
    }
  }, [tasks, taskId]);

  const employeesPerformance = reportData.employees_performance || [];

  if (tasksLoading) {
    return <div className="text-center py-5">Loading tasks...</div>;
  }

  if (tasksError) {
    return (
      <div className="alert alert-danger">
        Failed to load tasks: {tasksErrorObj?.message || "Unknown error"}
      </div>
    );
  }
  const employeeTaskReportColumns = [
    {
      header: "name",
      accessor: "employee_name",
      accessorFn: (row) => `${row.employee_name}`,
    },

    {
      header: "branch",
      accessor: "branch_name",
      accessorFn: (row) => `${row.branch_name}`,
    },
    {
      header: "quantity",
      accessor: "total_quantity",
      accessorFn: (row) => `${row.total_quantity}`,
    },
    {
      header: "Manager Rate",
      accessor: "average_manager_rating",
      accessorFn: (row) => `${row.average_manager_rating}`,
    },
    {
      header: "Task Achievement",
      accessor: "average_achieved_percentage",
      accessorFn: (row) => `${row.average_achieved_percentage}`,
    },
    {
      header: "Final Score",
      accessor: "average_final_score",
      accessorFn: (row) => `${row.average_final_score}`,
    },
  ];

  const employeeAttendanceExportColumns = [
    {
      header: "Employee Name",
      key: (row) => `${row.employee_name}`,
    },
    { header: "Branch", key: (row) => row.branch_name },
    { header: "Quantity", key: (row) => row.total_quantity },
    { header: "Manager Rate", key: (row) => row.average_manager_rating },
    {
      header: "Task Achievement",
      key: (row) => row.average_achieved_percentage,
    },
    { header: "Final Score", key: (row) => row.average_final_score },
  ];

  const handleExport = () => {
    if (employeesPerformance.length === 0) return;
    exportToExcel({
      data: employeesPerformance,
      columnsConfig: employeeAttendanceExportColumns,
      fileName: `task_report__${fromDate}__${toDate}`,
      sheetName: "TaskReport",
      columnWidths: [{ wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 20 }],
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div className="text-secondary">
          <strong>Task Report:</strong>{" "}
          <span>
            {formatDate(fromDate)} – {formatDate(toDate)}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0 text-secondary">Task</Form.Label>
          <select
            className="form-select form-control choicesjs"
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
            disabled={tasksLoading}
          >
            <option value="" disabled>
              Select Task
            </option>
            {tasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.job_title}
              </option>
            ))}
          </select>

          <Form.Label className="mb-0 text-secondary">From</Form.Label>
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setfromDate(e.target.value)}
            max={toDate}
            style={{ width: "150px" }}
            required
          />

          <Form.Label className="mb-0 text-secondary">To</Form.Label>
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => settoDate(e.target.value)}
            min={fromDate}
            max={formatYYYYMMDD(today)}
            style={{ width: "150px" }}
            required
          />
        </div>
        <Button variant="btn btn-success btn-sm" onClick={handleExport}>
          <i className="las la-file-excel mr-1"></i>
          {t("export") || "Export to Excel"}
        </Button>
      </div>

      <Tab.Container defaultActiveKey="home-tab">
        <Nav variant="tabs">
          <Nav.Item>
            <Nav.Link eventKey="home-tab">Employee achievement </Nav.Link>
          </Nav.Item>
          {/* <Nav.Item>
            <Nav.Link eventKey="second-tab">Branch achievement</Nav.Link>
          </Nav.Item> */}
          {/* <Nav.Item>
            <Nav.Link eventKey="contact-tab">Contact</Nav.Link>
          </Nav.Item> */}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="home-tab">
            <BasicTable
              data={
                Array.isArray(employeesPerformance) ? employeesPerformance : []
              }
              columns={employeeTaskReportColumns}
              title={`task_report`}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="second-tab">
            <BasicTable
              data={
                Array.isArray(employeesPerformance) ? employeesPerformance : []
              }
              columns={employeeTaskReportColumns}
              title={`task_report`}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="contact-tab">
            <p>
              Lorem Ipsum is simply dummy text of the printing and typesetting
              industry. Lorem Ipsum has been the industry's standard dummy text
              ever since the 1500s, when an unknown printer took a galley of
              type and scrambled it to make a type specimen book.
            </p>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};
export default AdminTaskReport;
